import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fmt } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Pencil, Trash, Package, Image as ImageIcon, X, CaretLeft, CaretRight, Sparkle, Eye, MegaphoneSimple, CurrencyDollar, ClockClockwise, MagnifyingGlass, Printer, Download, Rocket, Pause, Table } from "@phosphor-icons/react";
import { toast } from "sonner";
import POSModal from "@/components/POSModal";
import PasteImportModal from "@/components/PasteImportModal";
import { useSelection } from "@/hooks/useSelection";
import { useConfirm } from "@/hooks/useConfirm";
import BulkActionBar, { SelectAllCheckbox, RowCheckbox, runBulkAction, BulkBarButton, BulkBarSelect } from "@/components/BulkActionBar";
import { exportRowsToCsv, todayStamp } from "@/lib/csvExport";
import { AudienceModal } from "@/components/inventory/AudienceModal";
import { CatalogPicker } from "@/components/inventory/CatalogPicker";
import { GalleryLightbox } from "@/components/inventory/GalleryLightbox";
import { CATEGORIES, CONDICIONES, empty } from "@/components/inventory/shared";
import { ProductTable } from "@/components/inventory/ProductTable";
import { ProductCardList } from "@/components/inventory/ProductCardList";
import { ProductFormModal } from "@/components/inventory/ProductFormModal";

export default function Inventory() {
  const { sucursalId, user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [open, setOpen] = useState(false);
  const [gallery, setGallery] = useState(null); // {images: [], idx}
  const [audienceProduct, setAudienceProduct] = useState(null);
  const [posOpen, setPosOpen] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [posInitialId, setPosInitialId] = useState(null);
  const [businessName, setBusinessName] = useState("Tienda");
  const [moneda, setMoneda] = useState("MXN");
  const [postCounts, setPostCounts] = useState({}); // {product_id: count}
  const [query, setQuery] = useState("");
  const [catalogPickerOpen, setCatalogPickerOpen] = useState(false);
  const [sucursales, setSucursales] = useState([]);

  // Sucursales for the bulk "Reassign" dropdown. Loaded once on mount via
  // an inline async IIFE so React 19's set-state-in-effect rule doesn't fire.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/sucursales");
        if (!cancelled) setSucursales(r.data || []);
      } catch {
        // ignored — dropdown is optional UI
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) => {
      const hay = `${p.nombre || ""} ${p.categoria || ""} ${p.condicion || ""} ${p.descripcion || ""} ${p.para_que_sirve || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, query]);

  const load = async () => {
    const params = sucursalId ? { sucursal_id: sucursalId } : {};
    const [i, s, ps] = await Promise.all([
      api.get("/products", { params }),
      api.get("/products/stats", { params }),
      api.get("/posts", { params }).catch(() => ({ data: [] })),
    ]);
    setItems(i.data);
    setStats(s.data);
    const counts = {};
    (ps.data || []).forEach((p) => {
      if (!p.product_id) return;
      counts[p.product_id] = (counts[p.product_id] || 0) + 1;
    });
    setPostCounts(counts);
  };

  // Initial + sucursal-switch load. Wrapped to avoid the React 19
  // set-state-in-effect lint nag that fires on bare `load()` calls.
  useEffect(() => {
    let cancelled = false;
    const params = sucursalId ? { sucursal_id: sucursalId } : {};
    (async () => {
      try {
        const [i, s, ps] = await Promise.all([
          api.get("/products", { params }),
          api.get("/products/stats", { params }),
          api.get("/posts", { params }).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setItems(i.data);
        setStats(s.data);
        const counts = {};
        (ps.data || []).forEach((p) => {
          if (!p.product_id) return;
          counts[p.product_id] = (counts[p.product_id] || 0) + 1;
        });
        setPostCounts(counts);
      } catch {
        // ignored — non-critical for initial render
      }
    })();
    return () => { cancelled = true; };
  }, [sucursalId]);

  // Load business name + currency once for the POS ticket header
  useEffect(() => {
    api
      .get("/settings")
      .then((r) => {
        if (r.data?.nombre_negocio) setBusinessName(r.data.nombre_negocio);
        if (r.data?.moneda) setMoneda(r.data.moneda);
      })
      .catch(() => {});
  }, []);

  const onSave = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        costo: Number(form.costo) || 0,
        precio_venta: Number(form.precio_venta) || 0,
        cantidad: Number(form.cantidad) || 1,
      };
      if (editingId) {
        await api.put(`/products/${editingId}`, payload);
        toast.success(
          payload.auto_publish
            ? "Producto actualizado · generando borradores en automático…"
            : "Producto actualizado"
        );
      } else {
        await api.post("/products", payload);
        toast.success(
          payload.auto_publish
            ? "Producto agregado · la IA está generando borradores para IG/FB/WhatsApp…"
            : "Producto agregado al inventario"
        );
      }
      setOpen(false);
      setForm(empty);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    }
  };

  const onEdit = (p) => {
    setForm({
      nombre: p.nombre,
      categoria: p.categoria,
      descripcion: p.descripcion || "",
      costo: p.costo || 0,
      precio_venta: p.precio_venta || 0,
      cantidad: p.cantidad || 1,
      condicion: p.condicion || "usado",
      auto_publish: !!p.auto_publish,
      especificaciones: p.especificaciones || "",
    });
    setEditingId(p.id);
    setOpen(true);
  };

  const onDelete = async (p) => {
    if (!window.confirm(`Eliminar "${p.nombre}"?`)) return;
    await api.delete(`/products/${p.id}`);
    toast.success("Eliminado");
    load();
  };

  const selection = useSelection(filteredItems);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const { confirm, dialog: confirmDialog } = useConfirm();

  const suggestEspecificaciones = async () => {
    if (suggesting || !form.nombre) return;
    setSuggesting(true);
    try {
      // To produce a good suggestion the LLM needs the current draft saved;
      // if the user is editing an existing product we hit the endpoint
      // directly, otherwise we briefly create the product, ask the LLM, and
      // then keep the product around (the user is about to save anyway).
      let id = editingId;
      if (!id) {
        const { data: created } = await api.post("/products", {
          nombre: form.nombre,
          categoria: form.categoria,
          descripcion: form.descripcion || "",
          costo: Number(form.costo) || 0,
          precio_venta: Number(form.precio_venta) || 0,
          cantidad: Number(form.cantidad) || 1,
          condicion: form.condicion || "usado",
        });
        id = created.id;
        setEditingId(id);
      }
      const { data } = await api.post(`/products/${id}/suggest-especificaciones`);
      const sug = (data?.especificaciones || "").trim();
      if (sug) {
        setForm((f) => ({ ...f, especificaciones: sug }));
        toast.success(data?.source === "ai" ? "IA generó una base — edítala a tu gusto" : "Base sugerida generada");
      } else {
        toast.info("No se generó sugerencia. Intenta de nuevo o llena manualmente.");
      }
    } catch (e) {
      toast.error("No se pudo sugerir. Revisa el nombre y la conexión.");
    } finally {
      setSuggesting(false);
    }
  };
  const onBulkDelete = async () => {
    const ids = selection.selectedIds;
    if (!ids.length) return;
    const ok = await confirm({
      title: `Eliminar ${ids.length} producto${ids.length === 1 ? "" : "s"}`,
      description: "Esta acción no se puede deshacer. Las imágenes, publicaciones vinculadas y métricas asociadas se conservan, pero el producto desaparecerá del inventario.",
      confirmLabel: "Eliminar",
    });
    if (!ok) return;
    setBulkBusy(true);
    const { ok: done, fail } = await runBulkAction(ids, (id) => api.delete(`/products/${id}`));
    setBulkBusy(false);
    selection.clear();
    if (done) toast.success(`${done} producto(s) eliminado(s)`);
    if (fail) toast.error(`${fail} no se pudieron eliminar`);
    load();
  };

  const onBulkToggleAutoPublish = async (enable) => {    const ids = selection.selectedIds;
    if (!ids.length) return;
    const verb = enable ? "Activar" : "Pausar";
    const ok = await confirm({
      title: `${verb} auto-publicación en ${ids.length} producto${ids.length === 1 ? "" : "s"}`,
      description: enable
        ? "La IA generará borradores automáticamente para cada producto seleccionado. Verás los borradores en 'Bandeja auto'."
        : "Se detendrá la generación automática de borradores. Los borradores existentes no se eliminan.",
      confirmLabel: verb,
      tone: "default",
    });
    if (!ok) return;
    setBulkBusy(true);
    const { ok: done, fail } = await runBulkAction(ids, (id) =>
      api.put(`/products/${id}`, { auto_publish: enable }),
    );
    setBulkBusy(false);
    selection.clear();
    if (done) toast.success(`${done} producto(s) ${enable ? "con auto-publicación activa" : "pausados"}`);
    if (fail) toast.error(`${fail} no se pudieron actualizar`);
    load();
  };

  const onBulkExport = () => {
    const ids = new Set(selection.selectedIds);
    const rows = filteredItems.filter((p) => ids.has(p.id));
    if (!rows.length) return;
    exportRowsToCsv({
      filename: `productos-${todayStamp()}.csv`,
      headers: [
        { key: "nombre", label: "Nombre" },
        { key: "categoria", label: "Categoría" },
        { key: "condicion", label: "Condición" },
        { key: "cantidad", label: "Cantidad" },
        { key: "costo", label: "Costo" },
        { key: "precio_venta", label: "Precio venta" },
        { label: "Utilidad unitaria", value: (p) => (p.precio_venta - p.costo).toFixed(2) },
        { label: "Utilidad total", value: (p) => ((p.precio_venta - p.costo) * p.cantidad).toFixed(2) },
        { key: "descripcion", label: "Descripción" },
      ],
      rows,
    });
    toast.success(`${rows.length} producto(s) exportado(s) a CSV`);
  };

  const onBulkPrintSheet = () => {
    const ids = selection.selectedIds.slice(0, 9);
    if (!ids.length) return;
    if (selection.count > 9) {
      toast.info(`La hoja imprimible cabe hasta 9 productos — se imprimirán los primeros 9 de ${selection.count}.`);
    }
    navigate(`/catalogo-imprimible?ids=${ids.join(",")}`);
  };

  const onBulkReassignSucursal = async (newSucursalId) => {
    if (newSucursalId === "") return; // ignored "Reasignar sucursal…" placeholder
    const ids = selection.selectedIds;
    if (!ids.length) return;
    const target = newSucursalId === "__none__" ? null : newSucursalId;
    const targetLabel =
      target === null
        ? "Sin sucursal"
        : sucursales.find((s) => s.id === target)?.nombre || "—";
    const ok = await confirm({
      title: `Reasignar ${ids.length} producto${ids.length === 1 ? "" : "s"} a "${targetLabel}"`,
      description:
        "El producto pasará a contar para los reportes y filtros de la sucursal destino. Sus ventas históricas no cambian.",
      confirmLabel: "Reasignar",
      tone: "default",
    });
    if (!ok) return;
    setBulkBusy(true);
    const { ok: done, fail } = await runBulkAction(ids, (id) =>
      api.put(`/products/${id}`, { sucursal_id: target || "" }),
    );
    setBulkBusy(false);
    selection.clear();
    if (done) toast.success(`${done} producto(s) reasignado(s) a ${targetLabel}`);
    if (fail) toast.error(`${fail} no se pudieron reasignar`);
    load();
  };

  const goToPublic = (p) => {
    navigate(`/p/${p.slug || p.id}`, { state: { from: "/inventario" } });
  };

  const goToCreatePost = (p) => {
    navigate("/publicaciones", { state: { productId: p.id } });
  };

  const viewProductPosts = (p) => {
    navigate("/publicaciones", { state: { viewProductId: p.id } });
  };

  const sellNow = (p) => {
    setPosInitialId(p?.id || null);
    setPosOpen(true);
  };

  const viewHistory = (p) => {
    const params = new URLSearchParams({
      entity: "products",
      entity_id: p.id,
      label: p.nombre || "",
    });
    navigate(`/auditoria?${params.toString()}`);
  };

  const cats = stats?.por_categoria || [];

  return (
    <div className="px-4 sm:px-6 md:px-10 py-6 sm:py-10 max-w-[1400px]" data-testid="inventory-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8 md:mb-10">
        <div>
          <div className="overline text-klein">Inventario</div>
          <h1 className="h-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mt-2">
            Tus productos.
          </h1>
          <p className="text-muted2 mt-3 max-w-xl">
            Costos, ventas y utilidades calculados automáticamente por categoría.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            className="btn-ghost"
            onClick={() => setCatalogPickerOpen(true)}
            data-testid="btn-print-catalog"
            data-help="Crea una hoja A4 imprimible con hasta 9 productos, sus precios y un código QR para cada uno."
            disabled={items.length === 0}
          >
            <Printer size={18} /> Hoja imprimible
          </button>
          <button
            className="btn-ghost"
            onClick={() => {
              setPosInitialId(null);
              setPosOpen(true);
            }}
            data-testid="btn-pos"
            data-help="Abre la caja en mostrador para cobrar a un cliente que está físicamente contigo. Puedes agregar varios productos al mismo ticket."
          >
            <CurrencyDollar size={18} weight="bold" /> Venta en mostrador
          </button>
          <button
            className="btn-ghost"
            onClick={() => setPasteOpen(true)}
            data-testid="btn-paste-import"
            data-help="Pega varias filas desde Excel/Google Sheets y la app crea los productos en bloque. Acepta hasta 500 a la vez."
          >
            <Table size={16} weight="bold" /> Importar pegando
          </button>
          <button
            className="btn-klein"
            onClick={() => {
              setForm(empty);
              setEditingId(null);
              setOpen(true);
            }}
            data-testid="btn-add-product"
            data-help="Crea un producto manualmente. Si prefieres que la IA reconozca el objeto y sugiera precios, usa 'Escanear con AI'."
          >
            <Plus size={18} weight="bold" /> Nuevo producto
          </button>
        </div>
      </div>

      {/* Category bento */}
      {cats.length > 0 && (
        <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 border-t border-l border-zinc-200 mb-10">
          {cats.map((c) => (
            <div key={c.categoria} className="grid-cell border-r border-b border-zinc-200 p-5">
              <div className="overline">{c.categoria}</div>
              <div className="h-display text-2xl font-bold tracking-tight mt-2">{fmt(c.valor_venta)}</div>
              <div className="text-xs text-muted2 mt-1">
                Utilidad <span className="text-[#00C853]">{fmt(c.utilidad)}</span> · {c.margen_pct}% margen
              </div>
              <div className="text-[11px] text-muted2 mt-1">
                {c.unidades} unidades en {c.items} ítems
              </div>
            </div>
          ))}
        </section>
      )}

      {/* Quick search bar */}
      {items.length > 0 && (
        <div className="mb-4 flex items-center gap-2 max-w-lg" data-testid="inventory-search-wrap">
          <div className="relative flex-1">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted2 pointer-events-none" />
            <input
              type="text"
              className="input-flat pl-8"
              placeholder="Buscar por nombre, categoría, descripción…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="inventory-search"
              data-help="Filtra tu inventario al instante: escribe parte del nombre, categoría o descripción."
            />
          </div>
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="btn-ghost text-xs px-3 py-1.5"
              data-testid="inventory-search-clear"
            >
              <X size={14} /> {filteredItems.length} resultado{filteredItems.length === 1 ? "" : "s"}
            </button>
          )}
        </div>
      )}

      <BulkActionBar
        count={selection.count}
        noun="producto"
        pluralNoun="productos"
        onDelete={onBulkDelete}
        onClear={selection.clear}
        busy={bulkBusy}
        testid="inventory-bulk-bar"
        extraActions={
          <>
            <BulkBarButton
              onClick={() => onBulkToggleAutoPublish(true)}
              icon={Rocket}
              label="Activar auto"
              testid="inventory-bulk-bar-autopublish-on"
              disabled={bulkBusy}
            />
            <BulkBarButton
              onClick={() => onBulkToggleAutoPublish(false)}
              icon={Pause}
              label="Pausar auto"
              testid="inventory-bulk-bar-autopublish-off"
              disabled={bulkBusy}
            />
            {isAdmin && sucursales.length > 0 && (
              <BulkBarSelect
                value=""
                placeholder="Reasignar sucursal…"
                options={[
                  { value: "__none__", label: "— Sin sucursal —" },
                  ...sucursales.map((s) => ({ value: s.id, label: s.nombre })),
                ]}
                onChange={onBulkReassignSucursal}
                testid="inventory-bulk-bar-sucursal"
                disabled={bulkBusy}
              />
            )}
            <BulkBarButton
              onClick={onBulkExport}
              icon={Download}
              label="Exportar CSV"
              testid="inventory-bulk-bar-export"
              disabled={bulkBusy}
            />
            <BulkBarButton
              onClick={onBulkPrintSheet}
              icon={Printer}
              label="Imprimir hoja"
              testid="inventory-bulk-bar-print"
              disabled={bulkBusy}
            />
          </>
        }
      />
      {confirmDialog}

      {/* Table — desktop */}
      <ProductTable
        filteredItems={filteredItems}
        query={query}
        selection={selection}
        postCounts={postCounts}
        isAdmin={isAdmin}
        setGallery={setGallery}
        goToPublic={goToPublic}
        goToCreatePost={goToCreatePost}
        viewProductPosts={viewProductPosts}
        sellNow={sellNow}
        viewHistory={viewHistory}
        onEdit={onEdit}
        onDelete={onDelete}
      />

      {/* Mobile / tablet card list */}
      <ProductCardList
        filteredItems={filteredItems}
        query={query}
        selection={selection}
        postCounts={postCounts}
        isAdmin={isAdmin}
        setGallery={setGallery}
        setAudienceProduct={setAudienceProduct}
        goToPublic={goToPublic}
        goToCreatePost={goToCreatePost}
        viewProductPosts={viewProductPosts}
        sellNow={sellNow}
        viewHistory={viewHistory}
        onEdit={onEdit}
        onDelete={onDelete}
      />

      {/* Modal */}
      <ProductFormModal
        open={open}
        setOpen={setOpen}
        form={form}
        setForm={setForm}
        onSave={onSave}
        editingId={editingId}
        suggesting={suggesting}
        suggestEspecificaciones={suggestEspecificaciones}
      />

      {/* Gallery lightbox */}
      {gallery && (
        <GalleryLightbox
          gallery={gallery}
          setGallery={setGallery}
          onSwapped={load}
        />
      )}

      <POSModal
        open={posOpen}
        onClose={() => {
          setPosOpen(false);
          load();
        }}
        products={items}
        initialProductId={posInitialId}
        negocio={businessName}
        moneda={moneda}
      />

      <PasteImportModal
        open={pasteOpen}
        onClose={() => setPasteOpen(false)}
        onImported={() => load()}
      />

      {catalogPickerOpen && (
        <CatalogPicker
          products={items}
          onClose={() => setCatalogPickerOpen(false)}
        />
      )}

      {audienceProduct && (
        <AudienceModal
          product={audienceProduct}
          onClose={() => setAudienceProduct(null)}
        />
      )}
    </div>
  );
}
